/* eslint-disable @typescript-eslint/ban-ts-comment */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { fabric } from "fabric";
import cloneDeep from "lodash/cloneDeep";
import UploadDropzone from "../UploadDropzone";
import { FabricCanvasObject, Mapping } from "../../../../types";
import {
  scaleFabricObjFor3DRender,
  getCanvasRegionOutline,
  getImages,
} from "../../../../fabric/utils";
import useModel3D from "~/CustomizableToolPage/CustomizableTool/ModelViewer/ModelScene/three/hooks/useModel3D";
import useCustomizableContext from "~/CustomizableToolPage/useCustomizableContext";
import { CustomCanvasObjectProperties } from "~/CustomizableToolPage/fabric/constants";
import { User } from "~/common/Store/connectUserAndChat";

interface Props {
  user: User;
  showUploadDropzone: boolean;
  onObjectAdded?: (target: fabric.Object) => void;
  onObjectRemoved?: (target: fabric.Object) => void;
  onObjectModified?: (target: fabric.Object) => void;
  onSelectionChange?: (
    target: fabric.Object | null,
    hasActiveObj: boolean,
  ) => void;
}

const canvasId = "customization-canvas-element";

const CustomizableCanvas = ({
  user,
  showUploadDropzone,
  onObjectAdded,
  onObjectRemoved,
  onObjectModified,
  onSelectionChange,
}: Props) => {
  const {
    // @ts-ignore
    regionData,
    // @ts-ignore
    modelData,
    // @ts-ignore
    selectedRegion,
    // Fabric
    // @ts-ignore
    fabric: { canvas, regions, setFabricMounted, init, updateCanvas },
  } = useCustomizableContext();

  // @ts-ignore
  const [imageFillPaddingBottom, setImageFillPaddingBottom] = useState(100);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { loadTextureFromCanvas, loaded } = useModel3D();

  const getCanvasDimensions = useCallback(() => {
    const canvasSquareDefaultDimensions = 375;
    // set a minimum dimension length if half the default to not allow for unwanted narrow canvases
    const canvasMinimumDimensionLength = canvasSquareDefaultDimensions / 2;

    let canvasWidth = canvasSquareDefaultDimensions;
    let canvasHeight = canvasSquareDefaultDimensions;

    if (regionData[selectedRegion]) {
      const { height, width } = regionData[selectedRegion]!.mapping;

      if (width > height) {
        canvasHeight = (height / width) * canvasWidth;
      } else {
        canvasWidth = (width / height) * canvasHeight;
      }
    }

    // reset the canvas dimensions to a minimum of they are too narrow
    if (canvasHeight < canvasMinimumDimensionLength) {
      const newScale = canvasMinimumDimensionLength / canvasHeight;

      canvasHeight = canvasMinimumDimensionLength;
      canvasWidth *= newScale;
    } else if (canvasWidth < canvasMinimumDimensionLength) {
      const newScale = canvasMinimumDimensionLength / canvasWidth;

      canvasWidth = canvasMinimumDimensionLength;
      canvasHeight *= newScale;
    }

    return { height: canvasHeight, width: canvasWidth };
  }, [regionData, selectedRegion]);

  const updateCanvasContainerDimensions = useCallback(() => {
    const { height, width } = getCanvasDimensions();
    if (height > width) {
      setImageFillPaddingBottom((height / width) * 100);
    } else {
      setImageFillPaddingBottom((height / width) * 100);
    }
  }, [getCanvasDimensions]);

  const renderRegionTo3DModel = useCallback(
    (
      canvasToRender: fabric.Canvas,
      incomingCanvasObj: FabricCanvasObject,
      fileId: string,
      mapping: Mapping,
      isActiveRegion: boolean,
      isAlpha: boolean,
    ) => {
      const textureOriginalUrl =
        modelData.path + modelData.textures[fileId]?.file;

      // Render our new canvas
      canvasToRender.loadFromJSON(incomingCanvasObj, () =>
        fabric.Image.fromURL(
          textureOriginalUrl,
          (image) => {
            // @ts-ignore
            image.src = textureOriginalUrl;

            // We are creating a new canvas with the same dimensions as our texture that we are replacing
            canvasToRender.setDimensions({
              height: image.width!,
              width: image.height!,
            });

            canvasToRender.add(
              getCanvasRegionOutline(isAlpha, mapping, isActiveRegion),
            );

            canvasToRender.requestRenderAll();

            // Make our canvas background image the same as our texture so that nothing changes
            // Otherwise your bg would be blank
            canvasToRender.setBackgroundImage(image, () => {
              canvasToRender.renderAll.bind(canvasToRender);
              canvasToRender.renderAll();
            });

            // Add our canvas to the model
            loadTextureFromCanvas(
              textureOriginalUrl,
              false,
              isAlpha,
              // @ts-ignore
              canvasToRender.lowerCanvasEl,
            );
          },
          { crossOrigin: "anonymous" },
        ),
      );
    },
    [loadTextureFromCanvas, modelData.path, modelData.textures],
  );

  const transposeCanvasAndRender3D = useCallback(
    (canvasObject: FabricCanvasObject | null, regionId: number) => {
      // Create copies of our canvas object so that we don't modify our 2D canvas
      // These will be transposed and added to the 3D model

      const canvasObjectToRender = canvasObject ?? { version: "", objects: [] };

      const filteredCanvasObj = {
        ...canvasObjectToRender,
        // @ts-ignore
        objects: canvasObjectToRender.objects.filter((o) => !o?.isPlaceholder),
      };

      const incomingCanvasObj = cloneDeep(filteredCanvasObj);
      const alphaCanvasObj = cloneDeep(filteredCanvasObj);

      const isActiveRegion = Number(regionId) === Number(selectedRegion);

      // We only support a single texture for products with alpha maps.
      // We want to see that we have a single texture that each region is referencing as well as an alpha texture type
      // on one of our texture files.
      const fileIdCheck = Object.values(regionData)[0]?.mapping.fileId;
      const alphaFileId =
        Object.values(modelData.textures).find((t) => t.textureType === "Alpha")
          ?.id ?? -1;

      const hasAlphaMap =
        alphaFileId > 0 &&
        !Object.values(regionData).some(
          (r) => r.mapping.fileId !== fileIdCheck,
        );

      const { mapping } = regionData[regionId]!;

      /*
        There are times when a single 3d texture can hold multiple regions (ex. a shirt has left/right chest regions).
        This means that if we set this texture to a new canvas, our old canvas texture will be blown away.
        We want to display both regions, even if we're only actively customizing one.

        So, we need to find all customized regions that share this texture and rebuild one big canvas to display.
      */

      const regionsToReRender = Object.keys(regions).filter(
        (customizedRegionId) =>
          regionData[customizedRegionId]?.mapping.fileId === mapping.fileId &&
          Number(customizedRegionId) !== Number(regionId),
      );

      // Scale our canvas we're actually working with
      // @ts-ignore
      incomingCanvasObj.objects.forEach((o) => {
        scaleFabricObjFor3DRender(o, o.canvas!, mapping);
      });

      if (hasAlphaMap) {
        // @ts-ignore
        alphaCanvasObj.objects.forEach((o) => {
          scaleFabricObjFor3DRender(o, o.canvas!, mapping);
          if (o.type === "image") {
            // @ts-ignore
            o.filters = [
              new fabric.Image.filters.BlendColor({
                color: "white",
                mode: "add",
              }),
            ];
          }
        });
      }

      // We need access to the original canvas/mapping data so that our objects scale correctly
      regionsToReRender.forEach((id) => {
        // @ts-ignore
        regions[id]?.objects
          // @ts-ignore
          .filter((c) => !c?.isPlaceholder)
          .forEach((o) => {
            // Make a copy so that we don't resize our real canvas objects
            const temp = JSON.parse(JSON.stringify(o));
            scaleFabricObjFor3DRender(temp, o.canvas!, regionData[id]!.mapping);

            // We have to maintain a separate alpha map canvas for transparent objects
            // Images added to this canvas need a filter applied to them...
            if (hasAlphaMap) {
              const alphaTemp = JSON.parse(JSON.stringify(temp));
              if (temp.type === "image") {
                alphaTemp.filters = [
                  new fabric.Image.filters.BlendColor({
                    color: "white",
                    mode: "add",
                  }),
                ];
              }
              alphaCanvasObj.objects.push(alphaTemp);
            }

            incomingCanvasObj.objects.push(temp);
          });
      });

      const canvasToRender = new fabric.Canvas(
        document.createElement("canvas"),
        {
          enableRetinaScaling: false,
        },
      );

      // Render our new canvas
      if (regionData[regionId]?.mapping.fileId !== alphaFileId) {
        renderRegionTo3DModel(
          canvasToRender,
          incomingCanvasObj,
          mapping.fileId.toString(),
          mapping,
          isActiveRegion,
          false,
        );
      }

      if (hasAlphaMap) {
        const alphaCanvas = new fabric.Canvas(
          document.createElement("canvas"),
          {
            enableRetinaScaling: false,
          },
        );

        renderRegionTo3DModel(
          alphaCanvas,
          alphaCanvasObj,
          alphaFileId.toString(),
          mapping,
          isActiveRegion,
          true,
        );
      }
    },
    [
      modelData.textures,
      regionData,
      regions,
      renderRegionTo3DModel,
      selectedRegion,
    ],
  );

  const handleCanvasUpdate = useCallback(
    (canvasToRender: fabric.Canvas, regionId: number) => {
      // After a delete, we can lose the canvas, so we return...
      if (!canvasToRender) {
        return;
      }

      const incomingCanvasObj = canvasToRender.toObject(
        CustomCanvasObjectProperties,
      );

      transposeCanvasAndRender3D(incomingCanvasObj, regionId);
    },
    [transposeCanvasAndRender3D],
  );

  const handleCanvasChange = useCallback(
    (previousRegionId: number, newCanvas: fabric.Canvas) => {
      // This will ensure that only our new region will have a green outline
      if (previousRegionId && previousRegionId !== selectedRegion) {
        transposeCanvasAndRender3D(
          regions[previousRegionId] ?? null,
          Number(previousRegionId),
        );
      }

      handleCanvasUpdate(newCanvas, selectedRegion);
    },
    [handleCanvasUpdate, regions, selectedRegion, transposeCanvasAndRender3D],
  );

  useEffect(() => {
    updateCanvasContainerDimensions();
  }, [updateCanvasContainerDimensions]);

  useEffect(() => {
    if (loaded) {
      transposeCanvasAndRender3D(
        regions[selectedRegion] ?? null,
        selectedRegion,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    setFabricMounted();

    fabric.textureSize = 8192;

    const canvas = new fabric.Canvas(canvasRef.current, {
      containerClass: "customize__img",
      renderOnAddRemove: true,
    });

    if (selectedRegion) {
      init(
        regionData[selectedRegion]!,
        canvas,
        handleCanvasChange,
        onSelectionChange,
      );
    }

    return () => {
      canvas.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion]);

  if (canvas) {
    // Some listeners had to be moved out of the fabric init() because they dealt
    // with data that became stale. We have to clear the event listeners before attaching
    // them to the canvas to avoid firing them multiple times.

    // @ts-ignore
    if (canvas.__eventListeners?.["object:added"]?.length) {
      // @ts-ignore
      canvas.__eventListeners["object:added"] = [];
    }

    // @ts-ignore
    if (canvas.__eventListeners?.["object:removed"]?.length) {
      // @ts-ignore
      canvas.__eventListeners["object:removed"] = [];
    }

    // @ts-ignore
    if (canvas.__eventListeners?.["object:modified"]?.length) {
      // @ts-ignore
      canvas.__eventListeners["object:modified"] = [];
    }

    canvas.on("object:added", ({ target }) => {
      if (target?.canvas?.getContext?.()) {
        target.canvas.setActiveObject(target);
        target.canvas.renderAll();

        updateCanvas({ regionId: selectedRegion, canvas: target?.canvas });
        handleCanvasUpdate(target.canvas, selectedRegion);
        onObjectAdded?.(target);
      }
    });

    // @ts-ignore
    canvas.on("object:removed", ({ target }) => {
      if (!target?.canvas) return;
      updateCanvas({ regionId: selectedRegion, canvas: target?.canvas });
      handleCanvasUpdate(target.canvas, selectedRegion);
      onObjectRemoved?.(target);
    });

    // @ts-ignore
    canvas.on("object:modified", ({ target }) => {
      if (!target?.canvas) return;
      updateCanvas({ regionId: selectedRegion, canvas: target?.canvas });
      handleCanvasUpdate(target.canvas, selectedRegion);
      onObjectModified?.(target);
    });
  }

  useEffect(() => {
    // if there is scroll lets center the scroll since we always place objects in the center at first
    const customizationCanvas = document.getElementById("customization-canvas");
    const imageContainer = document.getElementsByClassName(
      "customize__img-container",
    )[0];

    if (customizationCanvas && imageContainer) {
      customizationCanvas.scrollLeft =
        imageContainer.clientWidth / 2 - customizationCanvas.clientWidth / 2;

      customizationCanvas.scrollTop =
        imageContainer.clientHeight / 2 - customizationCanvas.clientHeight / 2;
    }
  }, [selectedRegion]);

  return (
    <div className="customize__img-bg flex">
      <div
        id="customization-canvas"
        style={{ maxWidth: "376px", maxHeight: "400px" }}
        className="overflow-x-auto my-0 mx-auto w-full"
      >
        <div
          className="customize__img-container grid"
          style={{ width: `${getCanvasDimensions().width}px` }}
        >
          <div
            className="customize__img-fill"
            style={{ paddingBottom: `${imageFillPaddingBottom}%` }}
          />
          <canvas ref={canvasRef} id={canvasId} />

          {showUploadDropzone && !getImages(canvas).length && (
            <UploadDropzone user={user} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomizableCanvas;
